<template>
  <div v-if="noOptionalAbschnitte" class="alert alert-info my-4">
    <strong>Hinweis:</strong> Es gibt keine optionalen Abschnitte in diesem Vertrag. Klicken Sie auf "Weiter", um fortzufahren.
  </div>

  <div v-for="(abschnitt, idx) in editVertrag.textabschnitte.sort((a, b) => a.position - b.position)" :key="idx">
    <div v-if="displayAbschnitt(idx)" class="d-flex bg-white p-2 shadow-sm mb-3">
      <div class="flex-fill">
        <div class="form-check d-flex align-items-center">
          <input
            class="form-check-input"
            type="checkbox"
            :id="'abschnitt-' + idx + '-' + abschnitt.id"
            v-model="abschnitt.selected"
            @change="selectionChanged(abschnitt.textbausteinid)"
            :disabled="
              !checkAbschnittOptional(idx) ||
              forbiddenAbschnitte.map((item) => item.value).includes(abschnitt.textbausteinid) ||
              requiredAbschnitte.map((item) => item.value).includes(abschnitt.textbausteinid)
            "
          />
          <label
            class="form-check-label flex-fill ms-2 p-2 shadow-sm abschnittRow border border-light"
            :class="{ selectedAbschnitt: abschnitt.selected }"
            :for="'abschnitt-' + idx + '-' + abschnitt.id"
          >
            <SelectAbschnittRow :abschnitt="abschnitt" />
          </label>
        </div>

        <SelectSubabschnitte
          v-if="abschnitt.selected"
          :abschnitt="abschnitt"
          :editVertrag="editVertrag"
          :idx="idx"
          :requiredAbschnitte="requiredAbschnitte"
          :forbiddenAbschnitte="forbiddenAbschnitte"
          @checkRegeln="checkRegeln"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  //#region Imports
  import { PropType, computed, ref } from 'vue';
  import { useStore } from '@/store';

  import { Vertrag, TextAbschnitt } from '@/models/VertragModels';

  import SelectAbschnittRow from '../SelectAbschnittRow.vue';
  import SelectSubabschnitte from './SelectSubabschnitte.vue';
  import { Vorlage, VorlageAbschnittRegel, VorlageTextbaustein } from '@/models/VorlageModels';
  import { KeyValuePair } from '@/models/CommonModels';
  //#endregion

  const { editVertrag } = defineProps({
    editVertrag: {
      type: Object as PropType<Vertrag>,
      required: true,
    },
  });

  const store = useStore();
  const vorlage = computed(() => store.getters.vorlagen.find((v: Vorlage) => v.id == editVertrag.vorlageid));

  function selectionChanged(textbausteinid: number) {
    deselectChildren(textbausteinid);
    checkRegeln(textbausteinid, 0);
  }

  function deselectChildren(textbausteinid: number) {
    var abschnitt = editVertrag.textabschnitte.find((a) => a.textbausteinid == textbausteinid);
    if (abschnitt && abschnitt.selected == false) {
      abschnitt.abschnitte.forEach((sa: TextAbschnitt) => {
        const vorlageTextbaustein = vorlage.value?.vorlageTextbausteine.find((vtb) => vtb.textbausteinid == sa.textbausteinid);
        const regel = vorlageTextbaustein?.abschnittRegeln.find((ar) => ar.textbausteinId == sa.textbausteinid);
        if (regel != undefined && regel.optional) {
          sa.selected = false;
        }
      });
    }
  }

  function displayAbschnitt(abschnittIdx: number): boolean {
    const abschnitt = editVertrag.textabschnitte.sort((a, b) => a.position - b.position)[abschnittIdx];
    const regel = getAbschnittRegelByIndex(abschnittIdx);

    if (regel != undefined && !regel.aktiv) {
      return false;
    } else if ((regel != undefined && regel.optional) || abschnitt.abschnitte.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  const noOptionalAbschnitte = computed(() => {
    let optionaleAbchnitte = 0;

    for (let i = 0; i < editVertrag.textabschnitte.length; i++) {
      if (checkAbschnittOptional(i)) {
        optionaleAbchnitte++;
      }

      const abschnitt = editVertrag.textabschnitte.sort((a, b) => a.position - b.position)[i];
      if (abschnitt.abschnitte.length > 0) {
        for (let j = 0; j < abschnitt.abschnitte.length; j++) {
          if (checkAbschnittOptional(i, j)) {
            optionaleAbchnitte++;
          }
        }
      }
    }

    return optionaleAbchnitte == 0;
  });

  function checkAbschnittOptional(abschnittIdx: number, subabschnittIdx: number = -1): boolean {
    // const abschnitt = editVertrag.textabschnitte.sort((a, b) => a.position - b.position)[abschnittIdx];
    const regel = getAbschnittRegelByIndex(abschnittIdx, subabschnittIdx);
    // return regel?.optional ?? abschnitt.optional;
    return regel != undefined && regel.aktiv && regel.optional ? true : false;
  }

  function getAbschnittRegelByIndex(abschnittIdx: number, subabschnittIdx: number = -1): VorlageAbschnittRegel | undefined {
    const abschnitt = editVertrag.textabschnitte.sort((a, b) => a.position - b.position)[abschnittIdx];

    if (abschnitt) {
      if (subabschnittIdx >= 0) {
        const subabschnitt = abschnitt.abschnitte.sort((a, b) => a.position - b.position)[subabschnittIdx];
        return getAbschnittRegelByTextbausteinId(subabschnitt.textbausteinid);
      }

      // const vorlageTextbaustein = vorlage.value?.vorlageTextbausteine.find((vtb) => vtb.textbausteinid == abschnitt.textbausteinid);
      // return vorlageTextbaustein?.abschnittRegeln.find((ar) => ar.textbausteinId == abschnitt.textbausteinid);
      return getAbschnittRegelByTextbausteinId(abschnitt.textbausteinid);
    }

    return undefined;
  }

  const forbiddenAbschnitte = ref<Array<KeyValuePair>>([]);
  const requiredAbschnitte = ref<Array<KeyValuePair>>([]);

  function checkRegeln(textbausteinId: number, ebene: number = 0) {
    if (ebene >= 3) {
      // max 3 ebene
      return;
    }

    let abschnitt = getAbschnittByTextbausteinId(textbausteinId);
    const abschnittRegel = getAbschnittRegelByTextbausteinId(textbausteinId);

    if (abschnitt != undefined && abschnittRegel != undefined && abschnittRegel.regeln.length > 0) {
      for (const regel of abschnittRegel.regeln.sort((a, b) => a.textbausteinId - b.textbausteinId)) {
        const targetAbschnitt = getAbschnittByTextbausteinId(regel.textbausteinId);
        if (targetAbschnitt == undefined) {
          continue;
        }

        // select required Abschnitte
        if (regel.effekt == 1) {
          if (!abschnitt.selected) {
            // targetAbschnitt aus requiredAbschnitte entfernen, wenn parent abschnitt nicht selected ist
            requiredAbschnitte.value = requiredAbschnitte.value.filter((x) => !(x.key === regel.id && x.value === targetAbschnitt.textbausteinid));
            continue;
          }

          // wenn targetAbschnitt in forbiddenAbschnitte ist, dann aus forbiddenAbschnitte entfernen
          if (forbiddenAbschnitte.value.map((item) => item.value).includes(targetAbschnitt.textbausteinid)) {
            forbiddenAbschnitte.value = forbiddenAbschnitte.value.filter((x) => !(x.value === targetAbschnitt.textbausteinid));
          }

          targetAbschnitt.selected = true;

          // zu requiredAbschnitte hinzufügen
          if (!requiredAbschnitte.value.some((x) => x.key === regel.id && x.value === targetAbschnitt.textbausteinid)) {
            requiredAbschnitte.value.push({ key: regel.id, value: targetAbschnitt.textbausteinid });
          }

          checkRegeln(regel.textbausteinId, ebene + 1);
        } else if (regel.effekt == 2) {
          // deselect forbidden Abschnitte
          // targetAbschnitt aus forbiddenAbschnitte entfernen, wenn parent abschnitt nicht selected ist
          if (!abschnitt.selected) {
            forbiddenAbschnitte.value = forbiddenAbschnitte.value.filter((x) => !(x.key === regel.id && x.value === targetAbschnitt.textbausteinid));
            continue;
          }

          // return, wenn targetAbschnitt in requiredAbschnitte ist
          if (requiredAbschnitte.value.map((item) => item.value).includes(targetAbschnitt.textbausteinid)) {
            continue;
          }

          const targetAbschnittIdx = editVertrag.textabschnitte
            .sort((a, b) => a.position - b.position)
            .findIndex((a) => a.textbausteinid == targetAbschnitt.textbausteinid);

          targetAbschnitt.selected = false;
          deselectChildren(targetAbschnittIdx);

          // zu forbiddenAbschnitte hinzufügen
          if (!forbiddenAbschnitte.value.some((x) => x.key === regel.id && x.value === targetAbschnitt.textbausteinid)) {
            forbiddenAbschnitte.value.push({ key: regel.id, value: targetAbschnitt.textbausteinid });
          }

          checkRegeln(regel.textbausteinId, ebene + 1);
        }
      }
    }
  }

  function getAbschnittByTextbausteinId(textbausteinId: number): TextAbschnitt | undefined {
    for (const abs of editVertrag.textabschnitte) {
      if (abs && abs.textbausteinid == textbausteinId) {
        return abs;
      }

      if (abs.abschnitte != undefined && abs.abschnitte.length > 0) {
        for (const subAbs of abs.abschnitte) {
          if (subAbs && subAbs.textbausteinid == textbausteinId) {
            return subAbs;
          }
        }
      }
    }

    return undefined;
  }

  function getAbschnittRegelByTextbausteinId(textbausteinid: number): VorlageAbschnittRegel | undefined {
    // const vorlageTextbaustein = vorlage.value?.vorlageTextbausteine.find((vtb) => vtb.textbausteinid == textbausteinid);
    let vorlageTextbaustein: VorlageTextbaustein | undefined = undefined;

    for (const vtb of vorlage.value?.vorlageTextbausteine ?? []) {
      if (vtb.textbausteinid == textbausteinid) {
        vorlageTextbaustein = vtb;
        break;
      }

      if (vtb.textbaustein != undefined && vtb.textbaustein.abschnitte != undefined && vtb.textbaustein.abschnitte.length > 0) {
        for (const abs of vtb.textbaustein.abschnitte) {
          if (abs.id == textbausteinid) {
            vorlageTextbaustein = vtb;
            break;
          }
        }
      }

      if (vorlageTextbaustein != undefined) {
        break;
      }
    }

    return vorlageTextbaustein?.abschnittRegeln.find((ar) => ar.textbausteinId == textbausteinid) ?? undefined;
  }
</script>

<style scoped>
  .abschnittRow {
    background: white;
    cursor: pointer;
  }

  .abschnittRow:hover {
    /* background: #ffd1df;
  color: #770025; */
    background: #fff4af;
  }

  .selectedAbschnitt {
    /* background: #ffd1df;
  color: #770025; */
    background: #fff4af;
  }
</style>
